import PropTypes from 'prop-types';
import React from 'react';

// material-ui
import { makeStyles, useTheme } from '@material-ui/styles';
import { Box, Drawer, IconButton, useMediaQuery } from '@material-ui/core';

// third-party
import PerfectScrollbar from 'react-perfect-scrollbar';
import { BrowserView, MobileView } from 'react-device-detect';
import { IconMenu2 } from '@tabler/icons';

// project imports
import MenuList from './MenuList';
import LogoSection from '../LogoSection';
import { SIDEBAR_MIN_WIDTH, SIDEBAR_MAX_WIDTH } from './../../../store/constant';

// style constant — width comes from props (see useStyles(...) call below) so dragging the
// resize handle can resize the actual Drawer, not just a wrapper around a fixed-width one.
const useStyles = makeStyles((theme) => ({
    drawer: {
        [theme.breakpoints.up('md')]: {
            width: ({ width }) => width,
            flexShrink: 0
        }
    },
    drawerPaper: {
        width: ({ width }) => width,
        background: `
            radial-gradient(circle at 20% 0%, rgba(64, 196, 255, 0.12), transparent 42%),
            radial-gradient(circle at 90% 30%, rgba(124, 77, 255, 0.08), transparent 45%),
            linear-gradient(180deg, #f8fcff 0%, #f3f8ff 55%, #f8fbff 100%)
        `,
        backgroundAttachment: 'fixed',
        color: theme.palette.text.primary,
        borderRight: '1px solid rgba(148, 163, 184, 0.18)',
        boxShadow: '4px 0 18px rgba(15, 23, 42, 0.03)',
        position: 'fixed',
        top: 0,
        height: '100vh',
    },
    resizeHandle: {
        position: 'absolute',
        top: 0,
        right: -3,
        width: 6,
        height: '100%',
        cursor: 'col-resize',
        zIndex: 1250,
        background: 'transparent',
        transition: 'background-color 0.15s ease',
        '&:hover': {
            backgroundColor: 'rgba(25,118,210,0.25)',
        },
    },
    resizeHandleActive: {
        backgroundColor: 'rgba(25,118,210,0.4) !important',
    },
    brandArea: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: 88,
        paddingLeft: 16,
        paddingRight: 12,
        borderBottom: '1px solid rgba(148,163,184,0.1)',
    },
    ScrollHeight: {
        height: 'calc(100vh - 88px)',
        paddingLeft: '16px',
        paddingRight: '16px',
        paddingTop: '8px',
        [theme.breakpoints.down('sm')]: {
            height: 'calc(100vh - 59px)'
        },
    },
}));

//-----------------------|| SIDEBAR DRAWER ||-----------------------//

const Sidebar = ({ drawerOpen, drawerToggle, width, onWidthChange, onWidthCommit, window }) => {
    const classes = useStyles({ width });
    const theme = useTheme();
    const matchUpMd = useMediaQuery(theme.breakpoints.up('md'));
    const [dragging, setDragging] = React.useState(false);

    // Drag-to-resize. Reports the live width up to the parent on every move (so <main>'s margin
    // stays in sync instead of jumping once on mouseup), then reports once more on mouseup via
    // onWidthCommit so the parent can persist just the final value instead of writing to
    // localStorage on every pixel of movement.
    const handleResizeStart = (e) => {
        e.preventDefault();
        setDragging(true);
        const onMove = (moveEvent) => {
            const next = Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, moveEvent.clientX));
            onWidthChange(next);
        };
        const onUp = (upEvent) => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
            setDragging(false);
            const final = Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, upEvent.clientX));
            onWidthCommit(final);
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    };

    const drawer = (
        <React.Fragment>
            <div className={classes.brandArea}>
                <LogoSection />
            </div>
            <BrowserView>
                <PerfectScrollbar component="div" className={classes.ScrollHeight}>
                    <MenuList />
                </PerfectScrollbar>
            </BrowserView>
            <MobileView>
                <Box sx={{ px: 2 }}>
                    <MenuList />
                </Box>
            </MobileView>
        </React.Fragment>
    );

    const container = window !== undefined ? () => window().document.body : undefined;

    return (
        <nav className={classes.drawer} aria-label="mailbox folders">
            {/* Hamburger — outside Drawer so it stays visible when sidebar is collapsed */}
            <IconButton
                onClick={drawerToggle}
                size="small"
                sx={{
                    position: 'fixed',
                    top: 8,
                    left: drawerOpen ? width + 8 : 8,
                    display: { xs: 'none', md: 'inline-flex' },
                    zIndex: 1300,
                    backgroundColor: 'rgba(255,255,255,0.85)',
                    backdropFilter: 'blur(8px)',
                    border: '1px solid rgba(0,0,0,0.08)',
                    borderRadius: 2,
                    width: 32,
                    height: 32,
                    // No transition while actively dragging — animating toward every intermediate
                    // width during a drag makes the button visibly lag behind the cursor instead
                    // of tracking it. Still animates for the click-to-toggle open/close case.
                    transition: dragging ? 'none' : 'left 0.2s ease',
                    '&:hover': { backgroundColor: 'rgba(248,250,252,0.95)' },
                }}
            >
                <IconMenu2 stroke={1.5} size="1.2rem" />
            </IconButton>
            <Drawer
                container={container}
                variant={matchUpMd ? 'persistent' : 'temporary'}
                anchor="left"
                open={drawerOpen}
                onClose={drawerToggle}
                classes={{
                    paper: classes.drawerPaper
                }}
                ModalProps={{ keepMounted: true }}
                color="inherit"
            >
                {drawer}
                {/* Drag-to-resize handle — desktop only; the mobile drawer is a temporary overlay
                    that doesn't share layout space, so a fixed width there is fine. */}
                {matchUpMd && (
                    <div
                        className={`${classes.resizeHandle} ${dragging ? classes.resizeHandleActive : ''}`}
                        onMouseDown={handleResizeStart}
                        role="separator"
                        aria-orientation="vertical"
                        aria-label="Resize sidebar"
                    />
                )}
            </Drawer>
        </nav>
    );
};

Sidebar.propTypes = {
    drawerOpen: PropTypes.bool,
    drawerToggle: PropTypes.func,
    width: PropTypes.number.isRequired,
    onWidthChange: PropTypes.func.isRequired,
    onWidthCommit: PropTypes.func.isRequired,
    window: PropTypes.object
};

export default Sidebar;
